/**
 * Safely calculates the next step value for registration form submission.
 * Compares the registration step with the current URL step and returns
 * whichever is higher, advancing the URL step by 1 if it's current.
 *
 * Handles undefined, NaN, and non-numeric query params gracefully.
 */
export const getNextStep = (
  routerQueryStep: string | string[] | undefined,
  registrationStep: number | string | undefined
): number => {
  const currentUrlStep = Number(routerQueryStep ?? 1);
  const regStep = Number(registrationStep ?? 1);

  const safeUrlStep = Number.isNaN(currentUrlStep) || currentUrlStep < 1 ? 1 : currentUrlStep;
  const safeRegStep = Number.isNaN(regStep) || regStep < 1 ? 1 : regStep;

  if (registrationStep && safeRegStep > safeUrlStep) {
    return safeRegStep;
  }

  return safeUrlStep + 1;
};

/**
 * Safely parses a step value and clamps it between 1 and the given max.
 * Returns 1 for undefined, NaN, or non-positive values.
 */
export const clampStep = (value: string | string[] | number | undefined, max: number): number => {
  const parsed = Number(value ?? 1);
  if (Number.isNaN(parsed) || parsed < 1) return 1;
  return Math.min(parsed, max);
};

export const getErrorMessage = (error: any, defaultMessage: string): string => {
  // If error is already a string
  if (typeof error === 'string') {
    return error;
  }

  if (defaultMessage) {
    return defaultMessage;
  }

  // If error has a response (Axios / fetch-like)
  const { status, data } = error?.response ? error.response : error;
  switch (status) {
    case 401:
      return 'Unauthorized access. Please login again.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 409:
      return 'A conflict occurred. Please check your data and try again.';
    case 422:
      return data?.message || 'Validation failed. Please check your input.';
    case 429:
      return 'Too many requests. Please try again later.';
    case 500:
      return 'Internal server error. Please try again later.';
    case 502:
    case 503:
    case 504:
      return 'Service temporarily unavailable. Please try again later.';
    default:
      // If status code not handled, fall back to custom message if available
      if (data?.message && typeof data?.message === 'string') {
        return data.message;
      }
      if (error?.message && typeof error?.message === 'string') {
        return error.message;
      }
      return 'An unexpected error occurred. Please try again.';
  }
};
